import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Package, Search, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { reportsAPI } from '../../config/reports-api';
import { productsAPI } from '../../config/product-api';
import { formatOrderTotal } from '../../utils/orderDisplay';
import { helpers } from '../../utils/helpers';
import { ADMIN_PERMISSIONS } from '../../constants/permissions';
import './Reports.css';

const todayYmd = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const defaultFilters = () => ({
  filterType: 'today',
  date: todayYmd(),
  month: String(new Date().getMonth() + 1),
  year: String(new Date().getFullYear()),
  startDate: '',
  endDate: '',
  productId: 'All',
});

const Reports = ({ adminData, productFilter = null, onClearProductFilter }) => {
  const navigate = useNavigate();
  const canView = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_REPORTS)
    || helpers.hasPermission(adminData, ADMIN_PERMISSIONS.VIEW_ORDERS);

  const [activeTab, setActiveTab] = useState('daily-sale');
  const [filters, setFilters] = useState(() => {
    const base = defaultFilters();
    if (productFilter?.productId) {
      return { ...base, productId: String(productFilter.productId) };
    }
    return base;
  });
  const [appliedFilters, setAppliedFilters] = useState(() => {
    const base = defaultFilters();
    if (productFilter?.productId) {
      return { ...base, productId: String(productFilter.productId) };
    }
    return base;
  });
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [dailyBreakdown, setDailyBreakdown] = useState([]);
  const [summary, setSummary] = useState({ totalQty: 0, totalAmount: 0, productCount: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedProductMeta = useMemo(() => {
    if (productFilter?.productId) {
      return {
        productId: productFilter.productId,
        productNumericId: productFilter.productNumericId,
        productName: productFilter.productName,
      };
    }
    if (appliedFilters.productId === 'All') return null;
    const match = products.find((p) => String(p._id) === String(appliedFilters.productId));
    if (!match) return null;
    return {
      productId: match._id,
      productNumericId: match.productId,
      productName: match.name,
    };
  }, [appliedFilters.productId, productFilter, products]);

  const pageTitle = selectedProductMeta?.productName
    ? `Daily Sale Report of ${selectedProductMeta.productName}${
        selectedProductMeta.productNumericId != null
          ? ` (#${selectedProductMeta.productNumericId})`
          : ''
      }`
    : 'Daily Sale Report';

  const fetchReport = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError(null);
    try {
      const selected = appliedFilters.productId !== 'All'
        ? products.find((p) => String(p._id) === String(appliedFilters.productId))
        : null;

      const response = await reportsAPI.getDailySaleReport({
        filterType: appliedFilters.filterType,
        date: appliedFilters.filterType === 'date' ? appliedFilters.date : undefined,
        month: appliedFilters.filterType === 'month' ? appliedFilters.month : undefined,
        year: appliedFilters.filterType === 'month' ? appliedFilters.year : undefined,
        startDate: appliedFilters.filterType === 'range' ? appliedFilters.startDate : undefined,
        endDate: appliedFilters.filterType === 'range' ? appliedFilters.endDate : undefined,
        productId: appliedFilters.productId !== 'All' ? appliedFilters.productId : undefined,
        productNumericId: selected?.productId ?? productFilter?.productNumericId ?? undefined,
      });

      if (response.success) {
        setRows(response.data || []);
        setSummary(response.summary || { totalQty: 0, totalAmount: 0, productCount: 0 });
        setDailyBreakdown(response.dailyBreakdown || []);
      } else {
        setRows([]);
        setSummary({ totalQty: 0, totalAmount: 0, productCount: 0 });
        setDailyBreakdown([]);
        setError(response.error || 'Failed to fetch report');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch report');
      setRows([]);
      setDailyBreakdown([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, canView, products, productFilter?.productNumericId]);

  useEffect(() => {
    if (!canView) return;
    productsAPI.getProductsList({ page: 1, limit: 500 }).then((res) => {
      if (res.success) setProducts(res.data || []);
    });
  }, [canView]);

  useEffect(() => {
    if (productFilter?.productId) {
      const next = {
        ...defaultFilters(),
        productId: String(productFilter.productId),
      };
      setFilters(next);
      setAppliedFilters(next);
      setActiveTab('daily-sale');
    }
  }, [productFilter?.productId, productFilter?.productNumericId, productFilter?.productName]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const maxSelectableDate = todayYmd();

  const clampToToday = (ymd) => {
    if (!ymd) return ymd;
    return ymd > maxSelectableDate ? maxSelectableDate : ymd;
  };

  const handleFilterChange = (key, value) => {
    if (key === 'filterType') {
      setFilters((prev) => {
        const next = { ...prev, filterType: value };
        if (value === 'date') next.date = clampToToday(next.date || todayYmd());
        if (value === 'month') {
          let y = parseInt(next.year, 10) || currentYear;
          let m = parseInt(next.month, 10) || currentMonth;
          if (y > currentYear) y = currentYear;
          if (y === currentYear && m > currentMonth) m = currentMonth;
          next.year = String(y);
          next.month = String(m);
        }
        if (value === 'range') {
          next.startDate = clampToToday(next.startDate || todayYmd());
          next.endDate = clampToToday(next.endDate || todayYmd());
        }
        return next;
      });
      return;
    }

    if (key === 'date' || key === 'startDate' || key === 'endDate') {
      setFilters((prev) => ({ ...prev, [key]: clampToToday(value) }));
      return;
    }

    if (key === 'year') {
      setFilters((prev) => {
        let y = parseInt(value, 10) || currentYear;
        if (y > currentYear) y = currentYear;
        let m = parseInt(prev.month, 10) || currentMonth;
        if (y === currentYear && m > currentMonth) m = currentMonth;
        return { ...prev, year: String(y), month: String(m) };
      });
      return;
    }

    if (key === 'month') {
      setFilters((prev) => {
        const y = parseInt(prev.year, 10) || currentYear;
        let m = parseInt(value, 10) || currentMonth;
        if (y === currentYear && m > currentMonth) m = currentMonth;
        return { ...prev, month: String(m) };
      });
      return;
    }

    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    if (filters.filterType === 'range' && (!filters.startDate || !filters.endDate)) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (filters.filterType === 'date' && !filters.date) {
      toast.error('Please select a date');
      return;
    }
    if (filters.filterType === 'date' && filters.date > maxSelectableDate) {
      toast.error('Future dates are not allowed');
      return;
    }
    if (filters.filterType === 'range') {
      if (filters.startDate > maxSelectableDate || filters.endDate > maxSelectableDate) {
        toast.error('Future dates are not allowed');
        return;
      }
      if (filters.startDate > filters.endDate) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }
    if (filters.filterType === 'month') {
      const y = parseInt(filters.year, 10);
      const m = parseInt(filters.month, 10);
      if (y > currentYear || (y === currentYear && m > currentMonth)) {
        toast.error('Future months are not allowed');
        return;
      }
    }
    setAppliedFilters({ ...filters });
    if (filters.productId === 'All' && productFilter?.productId) {
      onClearProductFilter?.();
    }
  };

  const handleClearProductFilter = () => {
    const next = { ...filters, productId: 'All' };
    setFilters(next);
    setAppliedFilters(next);
    onClearProductFilter?.();
  };

  const handleProductClick = (row) => {
    if (!row?.productId) {
      toast.info('Product details unavailable for this row');
      return;
    }
    navigate('/dashboard', {
      state: {
        page: 'products',
        openProductId: row.productId,
      },
    });
  };

  const getImageParts = (image) => {
    if (!image) return { thumbnail: '', original: '' };
    if (typeof image === 'string') {
      const url = image.trim();
      return { thumbnail: url, original: url };
    }
    const original = String(image.image || image.url || '').trim();
    const thumbnail = String(image.thumbnail || image.thumb || '').trim();
    return { thumbnail, original };
  };

  /** Prefer thumbnail; fall back to original/full image when thumbnail is missing. */
  const getImageUrl = (image) => {
    const { thumbnail, original } = getImageParts(image);
    return thumbnail || original || '';
  };

  const handleReportImageError = (event, image) => {
    const { thumbnail, original } = getImageParts(image);
    const el = event.currentTarget;
    if (original && thumbnail && el.dataset.fallback !== '1') {
      el.dataset.fallback = '1';
      el.src = original;
      return;
    }
    el.style.display = 'none';
    const placeholder = el.nextElementSibling;
    if (placeholder?.classList?.contains('reports-product-thumb-empty')) {
      placeholder.style.display = 'inline-flex';
    }
  };

  const formatDisplayDate = (ymd) => {
    if (!ymd) return '-';
    const [y, m, d] = String(ymd).split('-');
    if (!y || !m || !d) return ymd;
    return `${d}/${m}/${y}`;
  };

  const showDailyBreakdown = dailyBreakdown.length > 0;

  if (!canView) {
    return (
      <div className="reports-page">
        <div className="reports-empty">You do not have permission to view reports.</div>
      </div>
    );
  }

  return (
    <div className="reports-page">
      <div className="reports-header">
        <div>
          <h1>{pageTitle}</h1>
          <p>Products ranked by quantity sold for the selected period</p>
        </div>
      </div>

      <div className="reports-tabs">
        <button
          type="button"
          className={`reports-tab ${activeTab === 'daily-sale' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily-sale')}
        >
          <BarChart3 size={16} />
          Daily Sale Report
        </button>
      </div>

      {activeTab === 'daily-sale' && (
        <>
          <div className="reports-stats-grid">
            <div className="reports-stat-card">
              <h3>Products</h3>
              <p>{summary.productCount ?? 0}</p>
            </div>
            <div className="reports-stat-card">
              <h3>Total Qty Sold</h3>
              <p>{summary.totalQty ?? 0}</p>
            </div>
            <div className="reports-stat-card">
              <h3>Total Amount</h3>
              <p>{formatOrderTotal(summary.totalAmount, adminData)}</p>
            </div>
          </div>

          <div className="reports-filter-card">
            <div className="reports-filter-row">
              <div className="reports-filter-group">
                <label>Date</label>
                <select
                  value={filters.filterType}
                  onChange={(e) => handleFilterChange('filterType', e.target.value)}
                  className="reports-filter-select"
                >
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last7days">Last 7 Days</option>
                  <option value="date">Custom Date</option>
                  <option value="month">Custom Month</option>
                  <option value="range">Custom Range</option>
                  <option value="lifetime">Lifetime</option>
                </select>
              </div>

              {filters.filterType === 'date' && (
                <div className="reports-filter-group">
                  <label>Select Date</label>
                  <input
                    type="date"
                    value={filters.date}
                    max={maxSelectableDate}
                    onChange={(e) => handleFilterChange('date', e.target.value)}
                    className="reports-filter-input"
                  />
                </div>
              )}

              {filters.filterType === 'month' && (
                <>
                  <div className="reports-filter-group">
                    <label>Month</label>
                    <select
                      value={filters.month}
                      onChange={(e) => handleFilterChange('month', e.target.value)}
                      className="reports-filter-select"
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1)
                        .filter((m) => {
                          const y = parseInt(filters.year, 10) || currentYear;
                          return y < currentYear || m <= currentMonth;
                        })
                        .map((m) => (
                          <option key={m} value={String(m)}>
                            {new Date(2000, m - 1, 1).toLocaleString('en', { month: 'long' })}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="reports-filter-group">
                    <label>Year</label>
                    <input
                      type="number"
                      value={filters.year}
                      onChange={(e) => handleFilterChange('year', e.target.value)}
                      className="reports-filter-input"
                      min="2020"
                      max={currentYear}
                    />
                  </div>
                </>
              )}

              {filters.filterType === 'range' && (
                <>
                  <div className="reports-filter-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={filters.startDate}
                      max={maxSelectableDate}
                      onChange={(e) => handleFilterChange('startDate', e.target.value)}
                      className="reports-filter-input"
                    />
                  </div>
                  <div className="reports-filter-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={filters.endDate}
                      max={maxSelectableDate}
                      onChange={(e) => handleFilterChange('endDate', e.target.value)}
                      className="reports-filter-input"
                    />
                  </div>
                </>
              )}

              <div className="reports-filter-group reports-filter-group-wide">
                <label>Product</label>
                <div className="reports-product-filter">
                  <select
                    value={filters.productId}
                    onChange={(e) => handleFilterChange('productId', e.target.value)}
                    className="reports-filter-select"
                  >
                    <option value="All">All Products</option>
                    {products.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name}{p.productId != null ? ` (#${p.productId})` : ''}
                      </option>
                    ))}
                  </select>
                  {(filters.productId !== 'All' || productFilter?.productId) && (
                    <button
                      type="button"
                      className="reports-clear-product-btn"
                      onClick={handleClearProductFilter}
                      title="Clear product filter"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>

              <div className="reports-filter-group">
                <label>&nbsp;</label>
                <button type="button" className="reports-apply-btn" onClick={handleApplyFilters}>
                  <Search size={16} />
                  Apply
                </button>
              </div>
            </div>
          </div>

          <div className="reports-table-card">
            {loading ? (
              <div className="reports-empty">Loading report...</div>
            ) : error ? (
              <div className="reports-empty reports-error">{error}</div>
            ) : rows.length === 0 ? (
              <div className="reports-empty">No sales found for this period</div>
            ) : (
              <div className="reports-table-wrapper">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product</th>
                      <th>Orders</th>
                      <th>Amount</th>
                      <th className="reports-qty-col">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={`${row.productId || row.name}-${index}`}>
                        <td>{index + 1}</td>
                        <td>
                          <button
                            type="button"
                            className="reports-product-link"
                            onClick={() => handleProductClick(row)}
                          >
                            {getImageUrl(row.image) ? (
                              <>
                                <img
                                  src={getImageUrl(row.image)}
                                  alt=""
                                  className="reports-product-thumb"
                                  onError={(e) => handleReportImageError(e, row.image)}
                                />
                                <span
                                  className="reports-product-thumb reports-product-thumb-empty"
                                  style={{ display: 'none' }}
                                  aria-hidden="true"
                                >
                                  <Package size={14} />
                                </span>
                              </>
                            ) : (
                              <span className="reports-product-thumb reports-product-thumb-empty">
                                <Package size={14} />
                              </span>
                            )}
                            <span className="reports-product-meta">
                              <strong>{row.name || 'Untitled product'}</strong>
                              {row.productNumericId != null && (
                                <small>ID #{row.productNumericId}</small>
                              )}
                            </span>
                          </button>
                        </td>
                        <td>{row.orderCount ?? 0}</td>
                        <td>{formatOrderTotal(row.amount, adminData)}</td>
                        <td className="reports-qty-col">
                          <span className="reports-qty-badge">{row.qty ?? 0}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!loading && !error && showDailyBreakdown && (
            <div className="reports-table-card">
              <div className="reports-section-heading">
                <h3>Date-wise Sale</h3>
                <p>Daily qty sold for the selected product in this period</p>
              </div>
              <div className="reports-table-wrapper">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Orders</th>
                      <th>Amount</th>
                      <th className="reports-qty-col">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyBreakdown.map((row, index) => (
                      <tr key={row.date || index}>
                        <td>{index + 1}</td>
                        <td>{formatDisplayDate(row.date)}</td>
                        <td>{row.orderCount ?? 0}</td>
                        <td>{formatOrderTotal(row.amount, adminData)}</td>
                        <td className="reports-qty-col">
                          <span className="reports-qty-badge">{row.qty ?? 0}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
